/**
 * Octobre Rose : compteurs en direct (don reversé, paires, coffrets Versatile
 * restants) lus sur le backend LFC. Un seul fetch par page, partagé par tous
 * les éléments marqués, rafraîchi toutes les 30 s et au retour sur l'onglet :
 * le nombre de coffrets affiché au client reste fidèle pendant qu'il hésite.
 *
 * Marqueurs :
 *  - [data-or-stat="donation|pairs|orders|boxes-remaining|boxes-given|boxes-total"]
 *  - [data-or-box] conteneur des mentions coffret (masqué tant que rien n'est
 *    chargé, et définitivement si le backend dit `hidden` : 24 h après
 *    épuisement, ou opération terminée)
 *  - [data-or-box-when="available|low|exhausted"] variantes de texte à
 *    l'intérieur, une seule visible à la fois. `low` = sous le seuil
 *    (data-or-low-threshold sur le conteneur, 20 par défaut).
 */
(() => {
  const config = window.OctobreRoseConfig || {};
  const ENDPOINT = config.endpoint || 'https://template.lesfrereschaussettes.com/octobre-rose/stats';
  const REFRESH_MS = 30000;
  const DEFAULT_LOW_THRESHOLD = 20;

  const formatInt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const formatEuros = (cents) =>
    (Number(cents || 0) / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  // Compteur animé « cagnotte » : monte de la valeur affichée jusqu'à la cible
  // (0 → montant au premier chargement, ancien → nouveau aux rafraîchissements).
  const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animateEuros = (el, targetCents) => {
    const from = Number(el.dataset.orValue || 0);
    const to = Number(targetCents || 0);
    if (el._orRaf) cancelAnimationFrame(el._orRaf);
    if (prefersReducedMotion || from === to) {
      el.dataset.orValue = String(to);
      el.textContent = formatEuros(to);
      return;
    }
    const duration = from === 0 ? 2200 : 900;
    const start = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      // Le timestamp rAF peut précéder `start` de quelques ms : borner à [0, 1]
      // sinon la première frame affiche un montant négatif.
      const t = Math.max(0, Math.min(1, (now - start) / duration));
      const current = Math.round(from + (to - from) * easeOutCubic(t));
      el.textContent = formatEuros(current);
      if (t < 1) el._orRaf = requestAnimationFrame(step);
      else { el.dataset.orValue = String(to); el._orRaf = null; }
    };
    el._orRaf = requestAnimationFrame(step);
  };

  const apply = (stats) => {
    document.querySelectorAll('[data-or-stat]').forEach((el) => {
      switch (el.dataset.orStat) {
        case 'donation':
          if (el.hasAttribute('data-or-animate')) animateEuros(el, stats.donationCents);
          else el.textContent = formatEuros(stats.donationCents);
          break;
        case 'pairs': el.textContent = formatInt(stats.pairs); break;
        case 'orders': el.textContent = formatInt(stats.orders); break;
        case 'boxes-remaining': el.textContent = formatInt(stats.boxesRemaining); break;
        case 'boxes-given': el.textContent = formatInt(stats.boxesGiven); break;
        case 'boxes-total': el.textContent = formatInt(stats.boxesTotal); break;
        default: break;
      }
    });

    document.querySelectorAll('[data-or-box]').forEach((box) => {
      const threshold = Number(box.dataset.orLowThreshold) || DEFAULT_LOW_THRESHOLD;
      const status = stats.boxStatus;
      box.dataset.orBoxStatus = status;
      box.hidden = status === 'hidden';
      if (status === 'hidden') return;
      const variant = status === 'exhausted' ? 'exhausted' : stats.boxesRemaining <= threshold ? 'low' : 'available';
      box.querySelectorAll('[data-or-box-when]').forEach((part) => {
        part.hidden = part.dataset.orBoxWhen !== variant;
      });
    });

    document.querySelectorAll('[data-or-loaded]').forEach((el) => { el.dataset.orLoaded = 'true'; });
    document.dispatchEvent(new CustomEvent('octobre-rose:stats', { detail: stats }));
  };

  // Mode démo (validation visuelle sur le thème brouillon) : ?or_demo=154 force
  // 154 paires commandées et tout ce qui en découle, côté navigateur uniquement,
  // mémorisé pour la session ; ?or_demo=0 pour revenir aux vraies valeurs.
  const demoPairs = (() => {
    try {
      const param = new URLSearchParams(location.search).get('or_demo');
      if (param !== null) {
        if (Number(param) > 0) sessionStorage.setItem('or_demo', String(Number(param)));
        else sessionStorage.removeItem('or_demo');
      }
      return Number(sessionStorage.getItem('or_demo') || 0);
    } catch (e) { return 0; }
  })();
  const withDemo = (stats) => {
    if (!demoPairs) return stats;
    const orders = Math.max(1, Math.round(demoPairs / 1.3));
    const boxesGiven = Math.min(stats.boxesTotal || 400, orders);
    const boxesRemaining = Math.max(0, (stats.boxesTotal || 400) - boxesGiven);
    return {
      ...stats,
      pairs: demoPairs,
      orders,
      donationCents: demoPairs * (stats.donationCentsPerPair || 300),
      boxesGiven,
      boxesRemaining,
      boxStatus: boxesRemaining > 0 ? 'available' : 'exhausted',
      demo: true,
    };
  };

  let inFlight = null;
  const refresh = () => {
    if (inFlight) return inFlight;
    inFlight = fetch(ENDPOINT, { cache: 'no-store', mode: 'cors' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(withDemo)
      .then(apply)
      .catch(() => { /* backend indisponible : on garde le dernier état affiché */ })
      .finally(() => { inFlight = null; });
    return inFlight;
  };

  const hasTargets = () => document.querySelector('[data-or-stat], [data-or-box]');
  const start = () => {
    if (!hasTargets()) return;
    refresh();
    setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.OctobreRose = { refresh };
})();
